import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { useTranslation } from "react-i18next";
import {
  Box,
  Flex,
  IconButton,
  Text,
  HStack,
  VStack,
  Alert,
  AlertIcon,
  Button,
} from "@chakra-ui/react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  AddIcon,
  MinusIcon,
  RepeatIcon,
} from "@chakra-ui/icons";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useLocation } from "react-router-dom";
import { useProjectsContext } from "@root/context/projects-context";
import { BrandedLogo } from "@root/components/branding";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DrawingPdfViewerProps {
  pdfUrl: string;
  payload: any;
}

// Memoized thumbnail component for performance
const ThumbnailItem = React.memo<{
  pageNumber: number;
  isActive: boolean;
  onClick: () => void;
  pdfUrl: string;
}>(({ pageNumber, isActive, onClick, pdfUrl }) => {
  return (
    <Box
      cursor="pointer"
      border={isActive ? "2px solid" : "1px solid"}
      borderColor={isActive ? "#0099E0" : "gray.300"}
      borderRadius="md"
      overflow="hidden"
      onClick={onClick}
      _hover={{
        transform: "scale(1.05)",
      }}
      transition="all 0.2s"
      height={"60px"}
      width={"100px"}
      flexShrink={0}
    >
      <Page
        pageNumber={pageNumber}
        scale={0.25}
        renderTextLayer={false}
        renderAnnotationLayer={false}
      />
    </Box>
  );
});

ThumbnailItem.displayName = "ThumbnailItem";

const DrawingPdfViewer: React.FC<DrawingPdfViewerProps> = React.memo(
  ({ pdfUrl, payload }) => {
    const { t } = useTranslation("common");
    const {
      pdfPageNumber: pageNumber,
      setPdfPageNumber: setPageNumber,
      onPageChange,
    } = useMainChatContext();
    const [numPages, setNumPages] = useState<number>(0);
    const [scale, setScale] = useState<number>(1);
    const [rotation, setRotation] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
    const thumbnailScrollRef = useRef<HTMLDivElement>(null);

    // Drag/Pan state
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(
      null
    );
    const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({
      x: 0,
      y: 0,
    });
    const pdfContainerRef = useRef<HTMLDivElement>(null);
    const { state } = useLocation();

    // Cleanup refs to track timeouts and animation frames
    const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
    const animationFrameRef = useRef<number | null>(null);
    const isMountedRef = useRef<boolean>(true);
    const scrollCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Check scroll state with debouncing
    const checkScrollState = useCallback(() => {
      if (!isMountedRef.current || !thumbnailScrollRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } =
        thumbnailScrollRef.current;
      const canScrollLeftNew = scrollLeft > 0;
      const canScrollRightNew = scrollLeft < scrollWidth - clientWidth - 1; // -1 for precision

      setCanScrollLeft(canScrollLeftNew);
      setCanScrollRight(canScrollRightNew);
    }, []);

    // Debounced scroll check
    const debouncedCheckScrollState = useCallback(() => {
      if (scrollCheckTimeoutRef.current) {
        clearTimeout(scrollCheckTimeoutRef.current);
      }
      scrollCheckTimeoutRef.current = setTimeout(() => {
        checkScrollState();
      }, 100);
    }, [checkScrollState]);

    const onDocumentLoadSuccess = useCallback(
      ({ numPages }: { numPages: number }) => {
        if (!isMountedRef.current) return;
        console.log("PDF loaded successfully with", numPages, "pages");
        console.log("PDF URL:", pdfUrl);
        setNumPages(numPages);
        setLoading(false);
        setError(null);
      },
      [pdfUrl]
    );

    const onThumbnailDocumentLoadSuccess = useCallback(() => {
      if (!isMountedRef.current) return;
      // Check scroll state after thumbnails are loaded
      const timeout1 = setTimeout(() => {
        if (isMountedRef.current) checkScrollState();
      }, 200);
      const timeout2 = setTimeout(() => {
        if (isMountedRef.current) checkScrollState();
      }, 800);

      timeoutRefs.current.push(timeout1, timeout2);

      // Also use requestAnimationFrame for more precise timing
      const rafId = requestAnimationFrame(() => {
        if (!isMountedRef.current) return;
        const timeout3 = setTimeout(() => {
          if (isMountedRef.current) checkScrollState();
        }, 100);
        timeoutRefs.current.push(timeout3);
      });
      animationFrameRef.current = rafId;
    }, [checkScrollState]);

    const onDocumentLoadError = useCallback(
      (error: Error) => {
        if (!isMountedRef.current) return;
        console.error("Error loading PDF:", error);
        console.error("PDF URL that failed:", pdfUrl);
        setLoading(false);
        setError(error.message);
      },
      [pdfUrl]
    );

    const { fetchDocument } = useProjectsContext();

    const retryLoading = useCallback(() => {
      if (!isMountedRef.current) return;
      if (state?.file) {
        fetchDocument(state?.file?.id);
      } else if (payload) {
        setPageNumber(payload?.page_no);
        fetchDocument(payload?.pdf_id);
      }
      setLoading(true);
      setError(null);
    }, [state?.file, payload, fetchDocument, setPageNumber]);

    const goToPreviousPage = useCallback(() => {
      if (!isMountedRef.current) return;
      const newPage = Math.max(pageNumber - 1, 1);
      setPageNumber(newPage);
      onPageChange?.(newPage);
    }, [pageNumber, setPageNumber, onPageChange]);

    const goToNextPage = useCallback(() => {
      if (!isMountedRef.current) return;
      const newPage = Math.min(pageNumber + 1, numPages);
      setPageNumber(newPage);
      onPageChange?.(newPage);
    }, [pageNumber, numPages, setPageNumber, onPageChange]);

    const goToPage = useCallback(
      (pageNum: number) => {
        if (!isMountedRef.current) return;
        const newPage = Math.max(1, Math.min(pageNum, numPages));
        setPageNumber(newPage);
        onPageChange?.(newPage);
      },
      [numPages, setPageNumber, onPageChange]
    );

    const zoomIn = useCallback(() => {
      setScale((prev) => Math.min(prev + 0.2, 3));
    }, []);

    const zoomOut = useCallback(() => {
      setScale((prev) => Math.max(prev - 0.2, 0.5));
    }, []);

    const rotate = useCallback(() => {
      setRotation((prev) => (prev + 90) % 360);
    }, []);

    const resetView = useCallback(() => {
      setScale(1);
      setRotation(0);
      setPanOffset({ x: 0, y: 0 }); // Reset pan position
    }, []);

    // Drag/Pan handlers
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }, []);

    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        if (!isDragging || !dragStart) return;

        e.preventDefault();
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;

        setPanOffset((prev) => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));

        setDragStart({ x: e.clientX, y: e.clientY });
      },
      [isDragging, dragStart]
    );

    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
      setDragStart(null);
    }, []);

    const handleMouseLeave = useCallback(() => {
      setIsDragging(false);
      setDragStart(null);
    }, []);

    // Touch handlers for mobile support
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
      e.preventDefault();
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: touch.clientX, y: touch.clientY });
      }
    }, []);

    const handleTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (!isDragging || !dragStart || e.touches.length !== 1) return;

        e.preventDefault();
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStart.x;
        const deltaY = touch.clientY - dragStart.y;

        setPanOffset((prev) => ({
          x: prev.x + deltaX,
          y: prev.y + deltaY,
        }));

        setDragStart({ x: touch.clientX, y: touch.clientY });
      },
      [isDragging, dragStart]
    );

    const handleTouchEnd = useCallback(() => {
      setIsDragging(false);
      setDragStart(null);
    }, []);

    // Thumbnail scroll functions
    const scrollThumbnailsLeft = useCallback(() => {
      if (thumbnailScrollRef.current) {
        thumbnailScrollRef.current.scrollBy({
          left: -120, // Scroll by approximately one thumbnail width + spacing
          behavior: "smooth",
        });
      }
    }, []);

    const scrollThumbnailsRight = useCallback(() => {
      if (thumbnailScrollRef.current) {
        thumbnailScrollRef.current.scrollBy({
          left: 120, // Scroll by approximately one thumbnail width + spacing
          behavior: "smooth",
        });
      }
    }, []);

    // Effect to check scroll state when thumbnails load
    useEffect(() => {
      if (numPages > 0 && isMountedRef.current) {
        // Use multiple timeouts to ensure thumbnails are fully rendered
        const timeout1 = setTimeout(() => {
          if (isMountedRef.current) checkScrollState();
        }, 100);
        const timeout2 = setTimeout(() => {
          if (isMountedRef.current) checkScrollState();
        }, 500);
        const timeout3 = setTimeout(() => {
          if (isMountedRef.current) checkScrollState();
        }, 1000);

        timeoutRefs.current.push(timeout1, timeout2, timeout3);

        return () => {
          clearTimeout(timeout1);
          clearTimeout(timeout2);
          clearTimeout(timeout3);
        };
      }
    }, [numPages, checkScrollState]);

    // Effect to check scroll state on window resize
    useEffect(() => {
      if (!isMountedRef.current) return;

      const handleResize = () => {
        if (isMountedRef.current) {
          debouncedCheckScrollState();
        }
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [debouncedCheckScrollState]);

    // Effect to prevent text selection during drag
    useEffect(() => {
      if (!isMountedRef.current) return;

      if (isDragging) {
        document.body.style.userSelect = "none";
        document.body.style.cursor = "grabbing";
      } else {
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }

      return () => {
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };
    }, [isDragging]);

    // Memoize thumbnail pages array
    const thumbnailPages = useMemo(() => {
      return Array.from(new Array(numPages), (_, index) => index + 1);
    }, [numPages]);

    // Reset state when PDF URL changes
    useEffect(() => {
      if (!isMountedRef.current) return;

      // Reset state when switching to a new PDF
      setNumPages(0);
      setLoading(true);
      setError(null);
      setScale(1);
      setRotation(0);
      setPanOffset({ x: 0, y: 0 });
      setIsDragging(false);
      setDragStart(null);
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }, [pdfUrl]);

    // Comprehensive cleanup on unmount
    useEffect(() => {
      isMountedRef.current = true;

      return () => {
        // Mark component as unmounted
        isMountedRef.current = false;

        // Clear all timeouts
        timeoutRefs.current.forEach((timeout) => {
          clearTimeout(timeout);
        });
        timeoutRefs.current = [];

        // Cancel animation frame
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }

        // Clear scroll check timeout
        if (scrollCheckTimeoutRef.current) {
          clearTimeout(scrollCheckTimeoutRef.current);
          scrollCheckTimeoutRef.current = null;
        }

        // Reset document body styles
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        // Reset state to prevent memory leaks
        setNumPages(0);
        setLoading(false);
        setError(null);
        setIsDragging(false);
        setDragStart(null);
        setPanOffset({ x: 0, y: 0 });
        setCanScrollLeft(false);
        setCanScrollRight(false);
      };
    }, []);

    if (error) {
      return (
        <VStack spacing={4} p={4}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {t("drawing_analysis.failedToLoadPdf", { error })}
          </Alert>
          <Button bgColor={"#40599B"} onClick={retryLoading} size="sm">
            {t("drawing_analysis.retryLoadingPdf")}
          </Button>
          <Text fontSize="sm" color="gray.600">
            {t("drawing_analysis.ifIssuePersists")}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {t("drawing_analysis.pdfUrlAccessibility")}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {t("drawing_analysis.networkConnection")}
          </Text>
          <Text fontSize="sm" color="gray.600">
            {t("drawing_analysis.pdfFileSize")}
          </Text>
          <Text fontSize="sm" color="gray.600" mt={2}>
            {t("drawing_analysis.currentUrl", { url: pdfUrl })}
          </Text>
        </VStack>
      );
    }

    return (
      <Box height={"100%"} padding={"16px"}>
        <VStack
          spacing={"20px"}
          width="100%"
          height="80%"
          padding={"16px"}
          bgColor={"white"}
          border={"1px solid #0099E0"}
          borderRadius={"16px"}
          boxShadow={"md"}
        >
          {/* Main PDF Viewer */}
          <Box
            width="100%"
            flex={1}
            overflow="hidden"
            position="relative"
            ref={pdfContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            cursor={isDragging ? "grabbing" : "grab"}
            userSelect="none"
            borderRadius={"12px"}
          >
            {/* Drag instruction overlay */}
            {!isDragging && (panOffset.x !== 0 || panOffset.y !== 0) && (
              <Box
                position="absolute"
                top="10px"
                left="50%"
                transform="translateX(-50%)"
                bg="rgba(64, 89, 155, 0.9)"
                color="white"
                px={3}
                py={1}
                borderRadius="md"
                fontSize="sm"
                zIndex={5}
                pointerEvents="none"
              >
                {t("drawing_analysis.dragToPan")}
              </Box>
            )}

            <Box
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              transform={`translate(${panOffset.x}px, ${panOffset.y}px)`}
              transition={isDragging ? "none" : "transform 0.1s ease-out"}
              opacity={isDragging ? 0.9 : 1}
            >
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                className={"document-main"}
                loading={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                    flexDirection="column"
                    gap="8px"
                  >
                    <BrandedLogo variant="full" width={100} height={60} />
                    <Text fontSize="14px" fontWeight="medium">
                      {t("drawing_analysis.loadingPdf")}
                    </Text>
                  </Box>
                }
              >
                <Box
                  borderRadius={"16px"}
                  overflow={"visible"}
                  display="inline-block"
                  minW="fit-content"
                  minH="fit-content"
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    rotate={rotation}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Box>
              </Document>
            </Box>
          </Box>

          <Box width={"100%"}>
            {/* Thumbnails Section */}
            {numPages > 0 && (
              <Box
                position="relative"
                width="100%"
                marginBottom={"30px"}
                px={canScrollLeft || canScrollRight ? "40px" : "0px"}
              >
                {/* Left Scroll Arrow */}
                {canScrollLeft && (
                  <Box
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={10}
                    bg="white"
                    borderRadius="50%"
                    boxShadow="lg"
                    border="1px solid #40599B"
                    _hover={{
                      bg: "gray.50",
                      transform: "translateY(-50%) scale(1.1)",
                      boxShadow: "xl",
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={scrollThumbnailsLeft}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="40px"
                    height="40px"
                  >
                    <ChevronLeftIcon color="#40599B" boxSize={6} />
                  </Box>
                )}

                {/* Right Scroll Arrow */}
                {canScrollRight && (
                  <Box
                    position="absolute"
                    right="0"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={10}
                    bg="white"
                    borderRadius="50%"
                    boxShadow="lg"
                    border="1px solid #40599B"
                    _hover={{
                      bg: "gray.50",
                      transform: "translateY(-50%) scale(1.1)",
                      boxShadow: "xl",
                    }}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={scrollThumbnailsRight}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="40px"
                    height="40px"
                  >
                    <ChevronRightIcon color="#40599B" boxSize={6} />
                  </Box>
                )}

                {/* Thumbnails Container */}
                <Box
                  ref={thumbnailScrollRef}
                  width="100%"
                  overflowX="auto"
                  onScroll={debouncedCheckScrollState}
                  css={{
                    "&::-webkit-scrollbar": {
                      height: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#f1f1f1",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#40599B",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "#2d3f6b",
                    },
                  }}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onThumbnailDocumentLoadSuccess}
                    loading={
                      <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="60px"
                        width="100%"
                      >
                        <Text>{t("drawing_analysis.loadingThumbnails")}</Text>
                      </Box>
                    }
                  >
                    <HStack
                      spacing={2}
                      minW="fit-content"
                      flexShrink={0}
                      py={2}
                    >
                      {thumbnailPages.map((thumbPageNum) => (
                        <ThumbnailItem
                          key={thumbPageNum}
                          pageNumber={thumbPageNum}
                          isActive={pageNumber === thumbPageNum}
                          onClick={() => goToPage(thumbPageNum)}
                          pdfUrl={pdfUrl}
                        />
                      ))}
                    </HStack>
                  </Document>
                </Box>
              </Box>
            )}

            {/* Controls Section */}
            {numPages > 0 && (
              <Flex
                width="100%"
                justifyContent="space-between"
                alignItems="center"
              >
                {/* Left Side - Zoom and Rotation Controls */}
                <HStack
                  spacing={3}
                  alignItems="center"
                  bgColor={"white"}
                  border={"1px solid #0099E0"}
                  borderRadius={"60px"}
                  p={"5px 20px"}
                >
                  {/* Zoom Controls Group */}
                  <HStack spacing={1} alignItems="center">
                    <IconButton
                      aria-label={t("drawing_analysis.zoomOut")}
                      icon={<MinusIcon />}
                      onClick={zoomOut}
                      size="sm"
                      variant="ghost"
                      isDisabled={scale <= 0.5}
                      color="gray.700"
                      _hover={{ bg: "gray.100" }}
                      _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                    />

                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      minWidth="40px"
                      textAlign="center"
                      color="gray.700"
                      userSelect="none"
                    >
                      {Math.round(scale * 100)}%
                    </Text>

                    <IconButton
                      aria-label={t("drawing_analysis.zoomIn")}
                      icon={<AddIcon />}
                      onClick={zoomIn}
                      size="sm"
                      variant="ghost"
                      isDisabled={scale >= 3}
                      color="gray.700"
                      _hover={{ bg: "gray.100" }}
                      _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                    />
                  </HStack>

                  {/* Separator Line */}
                  <Box width="1px" height="20px" bg="gray.300" mx={2} />

                  {/* Rotation and Reset Controls */}
                  <HStack spacing={2} alignItems="center">
                    <IconButton
                      aria-label={t("drawing_analysis.rotate")}
                      icon={<RepeatIcon />}
                      onClick={rotate}
                      size="sm"
                      variant="ghost"
                      color="gray.700"
                      _hover={{ bg: "gray.100" }}
                    />

                    <Box
                      onClick={resetView}
                      cursor={"pointer"}
                      color="gray.700"
                      _hover={{ color: "#40599B", transform: "scale(1.1)" }}
                      transition="all 0.2s"
                      title={t("drawing_analysis.resetView")}
                    >
                      ▣
                    </Box>
                  </HStack>
                </HStack>

                {/* Right Side - Pagination */}
                <HStack
                  spacing={1}
                  alignItems="center"
                  bgColor={"white"}
                  border={"1px solid #0099E0"}
                  borderRadius={"60px"}
                  p={"5px 20px"}
                >
                  <IconButton
                    aria-label={t("drawing_analysis.previousPage")}
                    icon={<ChevronLeftIcon />}
                    onClick={goToPreviousPage}
                    size="sm"
                    variant="ghost"
                    isDisabled={pageNumber <= 1}
                    color="gray.700"
                    _hover={{ bg: "gray.100" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />

                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    minWidth="50px"
                    textAlign="center"
                    color="gray.700"
                    userSelect="none"
                  >
                    {pageNumber} / {numPages}
                  </Text>

                  <IconButton
                    aria-label={t("drawing_analysis.nextPage")}
                    icon={<ChevronRightIcon />}
                    onClick={goToNextPage}
                    size="sm"
                    variant="ghost"
                    isDisabled={pageNumber >= numPages}
                    color="gray.700"
                    _hover={{ bg: "gray.100" }}
                    _disabled={{ opacity: 0.4, cursor: "not-allowed" }}
                  />
                </HStack>
              </Flex>
            )}
          </Box>
        </VStack>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if pdfUrl or payload changes
    return (
      prevProps.pdfUrl === nextProps.pdfUrl &&
      JSON.stringify(prevProps.payload) === JSON.stringify(nextProps.payload)
    );
  }
);

DrawingPdfViewer.displayName = "DrawingPdfViewer";

export default DrawingPdfViewer;
